import CareType, {
  CoachingCareTypes,
  TherapyCareTypes,
} from "@olivahealth/business/src/Core/domain/value-objects/CareType";
import cx from "classnames";
import Card from "../../atoms/Card";
import Text from "../../atoms/Text";
import {
  CircleIcon,
  UserPlaceholderIcon,
  CalendarIcon,
  ClockIcon,
  ExclamationIcon,
} from "../../atoms/Icons";
import Image from "../../../../../apps/web-client/src/components/common/Image";
import Button from "../../atoms/Button";
import tokens from "../../../theme/tokens";
import * as s from "./styles";

export type EventCardType = "check-in" | "therapy";

export interface EventCardProfessionalData {
  image: string | null;
  name: string;
  title: string | null;
}

interface Props {
  awaitingPayment?: string | null;
  buttonLabel: string;
  sessionStatus?: {
    label: string;
    color: string;
  };
  careType: CareType | null;
  date: string;
  shortDate: string;
  isActive?: boolean;
  onClickButton?: () => void;
  pendingProgressCheckSurvey: string | null;
  professional?: EventCardProfessionalData;
  timeRange: string;
  isCancelled?: boolean;
  checkoutUrl?: string | null;
}

export default function EventCard({
  awaitingPayment,
  buttonLabel,
  sessionStatus,
  date,
  isActive,
  checkoutUrl,
  onClickButton,
  pendingProgressCheckSurvey,
  professional,
  timeRange,
  shortDate,
  isCancelled,
  careType,
}: Readonly<Props>) {
  const isTherapy = careType && TherapyCareTypes.includes(careType);
  const isCoaching = careType && CoachingCareTypes.includes(careType);
  const isPendingProgressCheckSurvey =
    Boolean(pendingProgressCheckSurvey) && !isActive;
  const isAwaitingPayment = Boolean(awaitingPayment) && !isActive;

  const renderStatusMessage = (
    text: string,
    color: string,
    textColor: "info" | "warning",
  ) => (
    <div className={s.statusMessageWrapper}>
      <ExclamationIcon size={20} variant="triangle" color={color} />
      <Text color={textColor} size="sm">
        {text}
      </Text>
    </div>
  );

  if (isCancelled) {
    return (
      <Card
        className={cx("relative rounded-2xl bg-cover", {
          "bg-purple-950": isTherapy && !isActive,
          "bg-blue-900": isCoaching && !isActive,
        })}
      >
        <div className="p-3 flex flex-col gap-2">
          <span className={s.statusLabelWrapper}>
            <CircleIcon size={8} color={sessionStatus?.color} variant="solid" />
            <Text size="sm" color="light">
              {sessionStatus?.label}
            </Text>
          </span>
          <span className="line-through text-white">
            <Text color="white" weight="bold">
              {shortDate}
            </Text>
          </span>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className={cx("relative rounded-2xl bg-cover", {
        "bg-purple-950": isTherapy && !isActive,
        "bg-blue-900": isCoaching && !isActive,
        "bg-gradient-to-tl from-purple-400 via-purple-700 to-purple-950":
          isActive && isTherapy,
        "bg-gradient-to-tl from-blue-400 via-blue-700 to-blue-900":
          isActive && isCoaching,
      })}
    >
      <div className="p-4 flex flex-col relative z-10">
        {professional && (
          <div className="flex gap-2 items-center mb-4">
            {professional.image ? (
              <Image
                src={professional.image}
                alt="professional image"
                className="rounded-full object-cover"
                width={48}
                height={48}
              />
            ) : (
              <UserPlaceholderIcon size={48} color="white" />
            )}
            <div className="flex flex-col">
              {sessionStatus && (
                <span className={s.statusLabelWrapper}>
                  <CircleIcon
                    size={8}
                    color={sessionStatus.color}
                    variant="solid"
                  />
                  <Text size="sm" color="light">
                    {sessionStatus.label}
                  </Text>
                </span>
              )}
              <Text color="white" weight="bold">
                {professional.name}
              </Text>
            </div>
          </div>
        )}

        <div
          className={cx("flex justify-between p-3 rounded-2xl", {
            "bg-purple-900": isTherapy,
            "bg-blue-700": isCoaching,
          })}
        >
          <div className="flex items-center gap-2">
            <CalendarIcon color="white" />
            <Text color="white" weight="bold">
              {date}
            </Text>
          </div>
          <div className="flex items-center gap-2">
            <ClockIcon color="white" />
            <Text color="white" weight="bold">
              {timeRange}
            </Text>
          </div>
        </div>

        {(isActive ||
          isPendingProgressCheckSurvey ||
          (isAwaitingPayment && checkoutUrl)) && (
          <div className="mt-4">
            <Button width="full" variant="green" onClick={onClickButton}>
              {buttonLabel}
            </Button>
          </div>
        )}

        {isPendingProgressCheckSurvey &&
          renderStatusMessage(
            pendingProgressCheckSurvey ?? "",
            tokens.colors["status-info"][100],
            "info",
          )}

        {awaitingPayment &&
          renderStatusMessage(
            awaitingPayment,
            tokens.colors["status-yellow"][500],
            "warning",
          )}
      </div>
    </Card>
  );
}
